// Joint co-owner signing (checkout-joint-cosigner story).
// Pure helpers shared by the checkout joint block and its tests.

export const JOINT_OWNERSHIP_SUFFIX = Object.freeze({
  jtwros: "JTWROS",
  tic: "TIC",
});

const collapse = (value) => String(value || "").replace(/\s+/g, " ").trim();

export function jointFullName(firstName, lastName) {
  return collapse(`${firstName || ""} ${lastName || ""}`);
}

// "Taylor Investor & Jordan Coowner JTWROS" — registered under the primary
// investor's SSN (QA J-3 / Q1-10).
export function buildJointRegistration(primaryName, coownerName, ownershipType) {
  const suffix = JOINT_OWNERSHIP_SUFFIX[ownershipType] || JOINT_OWNERSHIP_SUFFIX.jtwros;
  return `${collapse(primaryName)} & ${collapse(coownerName)} ${suffix}`;
}

export function isValidEmail(value) {
  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(String(value || "").trim());
}

export function sameEmail(a, b) {
  const left = String(a || "").trim().toLowerCase();
  return Boolean(left) && left === String(b || "").trim().toLowerCase();
}

export function isAdultDob(value, today = new Date()) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || "").trim());
  if (!match) return false;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const dob = new Date(Date.UTC(year, month - 1, day));
  if (
    Number.isNaN(dob.getTime()) ||
    dob.getUTCFullYear() !== year ||
    dob.getUTCMonth() !== month - 1 ||
    dob.getUTCDate() !== day ||
    year < 1900
  ) {
    return false;
  }
  let age = today.getUTCFullYear() - year;
  const beforeBirthday =
    today.getUTCMonth() + 1 < month ||
    (today.getUTCMonth() + 1 === month && today.getUTCDate() < day);
  if (beforeBirthday) age -= 1;
  return age >= 18 && age < 125;
}

// The ``joint_cosigner`` block of the checkout request. The co-owner's date
// of birth goes to the server once (encrypted at rest there) and is never
// written to the checkout draft. Their SSN is not collected.
export function buildJointCosignerPayload({ details, primarySignature, trustCompanyId }) {
  const digits = (value) => String(value || "").replace(/\D/g, "");
  return {
    ownership_type: details.ownership_type,
    first_name: collapse(details.first_name),
    last_name: collapse(details.last_name),
    email: String(details.email || "").trim(),
    phone: digits(details.phone),
    date_of_birth: String(details.date_of_birth || "").trim(),
    primary_signature: collapse(primarySignature),
    trust_company_id: trustCompanyId || null,
  };
}

// What the confirmation screen shows comes from the server's checkout
// response, never from what the page assumed: whether the investment is
// waiting for the co-owner and whether the invitation email went out.
export function jointCosignResultFromCheckoutResponse(response) {
  const investments = response?.data?.investments || [];
  const joint = investments.find((item) => item && item.payment_pending_joint_cosigner);
  if (!joint) return null;
  return {
    investmentId: joint.id ?? null,
    firstName: (joint.joint_cosign && joint.joint_cosign.cosigner_first_name) || "",
    inviteSent: joint.joint_cosign_invite_sent !== false,
    paymentMethod: joint.payment_method || null,
  };
}

// Reads the single-use token from the URL fragment (#t=...) and removes the
// fragment from the address bar and history straight away, so it never
// reaches analytics, error reports or the Referer header.
export function takeTokenFromLocation(win = typeof window !== "undefined" ? window : null) {
  if (!win || !win.location) return "";
  const hash = String(win.location.hash || "").replace(/^#/, "");
  if (!hash) return "";
  let token = "";
  try {
    token = new URLSearchParams(hash).get("t") || "";
  } catch (error) {
    token = "";
  }
  if (win.history && typeof win.history.replaceState === "function") {
    const clean = `${win.location.pathname}${win.location.search || ""}`;
    try {
      win.history.replaceState(win.history.state, "", clean);
    } catch (error) {
      // Some embedded browsers block replaceState; the token is still only
      // in the fragment, which is never sent to a server.
    }
  }
  return token.trim();
}

const TOKEN_STORAGE_KEY = "invown:joint-cosign-token";

// Called first thing in main.js, before analytics or error reporting start:
// on the co-sign page, move the token out of the URL into this tab's
// sessionStorage (so a reload still works) and strip the fragment.
export function captureJointCosignTokenEarly(win = typeof window !== "undefined" ? window : null) {
  if (!win || !win.location || !/\/joint-cosign\/?$/.test(win.location.pathname || "")) {
    return;
  }
  const token = takeTokenFromLocation(win);
  if (!token) return;
  try {
    win.sessionStorage.setItem(TOKEN_STORAGE_KEY, token);
  } catch (error) {
    // Storage blocked: keep it in memory for this page load.
    win.__invownJointCosignToken = token;
  }
}

// The token for the co-sign page: from the URL fragment if still there,
// else from what main.js captured.
export function readJointCosignToken(win = typeof window !== "undefined" ? window : null) {
  if (!win) return "";
  const fromHash = takeTokenFromLocation(win);
  if (fromHash) {
    try {
      win.sessionStorage.setItem(TOKEN_STORAGE_KEY, fromHash);
    } catch (error) {
      win.__invownJointCosignToken = fromHash;
    }
    return fromHash;
  }
  try {
    const stored = win.sessionStorage.getItem(TOKEN_STORAGE_KEY);
    if (stored) return stored;
  } catch (error) {
    // fall through
  }
  return win.__invownJointCosignToken || "";
}

export function forgetJointCosignToken(win = typeof window !== "undefined" ? window : null) {
  if (!win) return;
  try {
    win.sessionStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch (error) {
    // nothing stored
  }
  win.__invownJointCosignToken = "";
}

// Strips the co-sign token from any URL-like string (Sentry scrubbing).
export function scrubJointCosignToken(value) {
  if (typeof value !== "string" || !value) return value;
  return value
    .replace(/(joint-cosign[^#\s"']*)#[^\s"']*/g, "$1#[redacted]")
    .replace(/([?&#]t(?:oken)?=)[A-Za-z0-9_-]{16,}/g, "$1[redacted]");
}
